/**
 * apps/web :: components/whatsnext :: AncillarySection
 *
 * Section 7 of the post-pass journey (ROADS-431).
 *
 * Three ancillary opportunities most learners only encounter at the
 * DMV counter:
 *
 *   1. Motor-voter — the National Voter Registration Act of 1993 lets
 *      learners register to vote at the same DMV visit in 40+ states.
 *      Eligibility (US citizenship + age 18 by next election) is the
 *      driver's responsibility; we surface the option, not advice.
 *   2. Organ-donor registry — opt-in registration on the DL application
 *      in every US state. Learners can opt in or out at any time.
 *   3. REAL ID upgrade — REAL-ID-compliant DLs are required for
 *      domestic-flight TSA boarding starting May 7, 2025 (already in
 *      effect on this page). Learners can choose REAL ID or non-REAL
 *      ID at application time depending on the documents they bring.
 *
 * Status-agnostic per ROADS-436: the motor-voter card explicitly says
 * "if you are eligible" and never assumes the user's citizenship.
 */
import { useTranslation } from "react-i18next";

const ITEM_KEYS = ["motorVoter", "organDonor", "realId"] as const;

export default function AncillarySection() {
  const { t } = useTranslation();
  return (
    <section
      data-testid="whatsnext-section-ancillary"
      aria-labelledby="whatsnext-ancillary-heading"
      className="rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
    >
      <h2
        id="whatsnext-ancillary-heading"
        className="font-display text-xl font-bold text-brand-primary"
      >
        {t("whatsnext.ancillary.heading")}
      </h2>
      <p className="mt-sm text-neutral-700">
        {t("whatsnext.ancillary.intro")}
      </p>

      <ul
        className="mt-md space-y-sm"
        data-testid="whatsnext-ancillary-items"
      >
        {ITEM_KEYS.map((key) => (
          <li
            key={key}
            className="rounded-md border border-neutral-200 bg-slate-50 p-sm"
            data-testid={`whatsnext-ancillary-${key}`}
          >
            <h3 className="font-semibold text-brand-primary">
              {t(`whatsnext.ancillary.items.${key}.title`)}
            </h3>
            <p className="mt-xs text-sm text-neutral-800">
              {t(`whatsnext.ancillary.items.${key}.body`)}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}
